// frontend/src/components/ChatWidget.jsx
/**
 * ChatWidget — floating agent assistant (P15-G11).
 *
 * Collapsed: a circular chat-bubble button pinned bottom-right (with an unread
 * dot when an assistant reply arrived while collapsed). Expanded: a 320×480
 * panel above the button with a message list, agent-switch links, and an input.
 *
 * Sends each turn to POST /api/agents/chat, threading the returned session_id so
 * the conversation continues. Hidden on /admin/* routes (the admin shell has its
 * own surface). Mounted in Layout.jsx, so it shows on all authenticated pages.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { sendChatMessage, endSession, submitCsat } from "../api/agents";

/** User-facing agent personas (mara is operator-only and not offered here). */
const AGENT_LABELS = { aria: "Aria", dev: "Dev", sam: "Sam", mara: "Mara" };
const SWITCHABLE_AGENTS = ["aria", "dev", "sam"];

const agentLabel = (a) => AGENT_LABELS[a] || "Aria";
const agentInitial = (a) => agentLabel(a).charAt(0);

/** Short HH:MM timestamp for a message. */
function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Render a pending_action dict as a human-readable one-liner for the card. */
function describeAction(action) {
  if (!action || typeof action !== "object") return "";
  return Object.entries(action)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
    .join(" · ");
}

/**
 * @param {object} props
 * @param {number} [props.csatDelayMs] Idle delay before the CSAT star prompt
 *   appears, in ms (default 5000). Overridable so tests don't wait 5 seconds.
 */
function ChatWidget({ csatDelayMs = 5000 }) {
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("aria");
  const [agentHint, setAgentHint] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  // The agent we're transitioning TO during a handoff, or null (P16-G0e).
  const [handoffTo, setHandoffTo] = useState(null);
  // CSAT prompt lifecycle (P18-G2): null → "prompt" → "submitting" → "done".
  const [csatState, setCsatState] = useState(null);

  const lastMessageRef = useRef(null); // remembered for the retry button
  const scrollRef = useRef(null);
  const prevAgentRef = useRef(null); // last agent shown from a reply (handoff detection)
  const handoffTimerRef = useRef(null);

  // Auto-scroll the message list to the newest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Clear any pending handoff-banner timer on unmount.
  useEffect(() => () => clearTimeout(handoffTimerRef.current), []);

  // CSAT (P18-G2): five seconds after the last assistant message — i.e. the
  // conversation has gone quiet — offer a star rating. Re-arms whenever a new
  // reply arrives (the effect re-runs on `messages`). Skipped once the user has
  // been prompted or has rated, and while an action card is awaiting a verdict.
  useEffect(() => {
    if (csatState !== null || !sessionId) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    if (last.responseType === "action_pending") return;
    const t = setTimeout(() => setCsatState("prompt"), csatDelayMs);
    return () => clearTimeout(t);
  }, [messages, sessionId, csatState, csatDelayMs]);

  // Rate the conversation: close the session to get its case id, then post the
  // score. Always lands on "done" so the thank-you shows even if a call fails.
  async function rateConversation(score) {
    setCsatState("submitting");
    try {
      const { case_id } = await endSession(sessionId, "resolved");
      await submitCsat(case_id, score);
    } catch {
      /* swallow — a failed rating shouldn't trap the user on the star prompt */
    } finally {
      setCsatState("done");
    }
  }

  // Let other pages open the widget pre-filled (e.g. "Chat with Aria" on a
  // ticket — P16-G10) via a window event, so they stay decoupled from it.
  useEffect(() => {
    const onOpenChat = (e) => {
      const { message, agent } = e.detail || {};
      setIsOpen(true);
      setHasUnread(false);
      if (agent) {
        setCurrentAgent(agent);
        setAgentHint(agent);
        setSessionId(null);
        prevAgentRef.current = agent;
      }
      if (message) setInput(message);
    };
    window.addEventListener("qrflow:open-chat", onOpenChat);
    return () => window.removeEventListener("qrflow:open-chat", onOpenChat);
  }, []);

  // Briefly show "Connecting you with …" in the header when an agent hands the
  // conversation to another (P16-G0e), then settle on the new agent.
  function triggerHandoff(agent) {
    clearTimeout(handoffTimerRef.current);
    setHandoffTo(agent);
    handoffTimerRef.current = setTimeout(() => setHandoffTo(null), 1500);
  }

  // The widget never appears in the admin shell.
  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  const openPanel = () => {
    setIsOpen(true);
    setHasUnread(false);
  };

  // Fold one agent reply into state. A reply may itself be an action_pending
  // card (P16-G0d), carried on responseType/pendingAction for rendering.
  function applyResponse(data) {
    setSessionId(data.session_id);
    // A server-driven agent change between turns is a handoff — animate it.
    // Skip the very first reply (no prior agent) so initial routing is silent.
    if (
      prevAgentRef.current &&
      data.agent &&
      data.agent !== prevAgentRef.current
    ) {
      triggerHandoff(data.agent);
    }
    prevAgentRef.current = data.agent;
    setCurrentAgent(data.agent);
    setAgentHint(null);
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: data.response,
        time: new Date(),
        responseType: data.response_type,
        pendingAction: data.pending_action,
      },
    ]);
    if (!isOpen) setHasUnread(true);
  }

  async function sendText(text) {
    lastMessageRef.current = text;
    // A new message means the user is still here — dismiss any pending star
    // prompt (but never re-open it once they've already rated).
    if (csatState === "prompt") setCsatState(null);
    setMessages((m) => [...m, { role: "user", content: text, time: new Date() }]);
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await sendChatMessage({
        message: text,
        sessionId,
        agent: agentHint,
      });
      applyResponse(data);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }

  // Confirm/Cancel a pending action (P16-G0d). Freezes the card (so its buttons
  // can't fire twice) and sends the verdict back to the agent as a new turn.
  async function resolvePendingAction(msgIndex, confirm, pendingAction) {
    setMessages((m) =>
      m.map((x, i) =>
        i === msgIndex
          ? { ...x, resolved: confirm ? "confirmed" : "cancelled" }
          : x
      )
    );
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await sendChatMessage({
        sessionId,
        agent: agentHint,
        confirmAction: confirm,
        pendingAction: confirm ? pendingAction : null,
      });
      applyResponse(data);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSend = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    sendText(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetry = () => {
    if (lastMessageRef.current && !isLoading) {
      sendText(lastMessageRef.current);
    }
  };

  // Switching agents starts a fresh session routed to the chosen persona; the
  // visible history is kept for continuity (the backend pins an existing
  // session's agent, so a clean session is what makes the switch take effect).
  const switchAgent = (a) => {
    setCurrentAgent(a);
    setAgentHint(a);
    setSessionId(null);
    setHasError(false);
    setCsatState(null);
    // A deliberate user switch isn't a handoff — sync the ref so the next
    // reply (same agent) doesn't trigger the "Connecting you with …" banner.
    prevAgentRef.current = a;
    setHandoffTo(null);
  };

  // ── Collapsed: floating button ──────────────────────────────────────────── //
  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={openPanel}
        aria-label="Open chat assistant"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-colors hover:bg-blue-700"
      >
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.17-3.5A7.5 7.5 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        {hasUnread && (
          <span
            data-testid="chat-unread-dot"
            className="absolute right-1 top-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white"
          />
        )}
      </button>
    );
  }

  // ── Expanded: chat panel ────────────────────────────────────────────────── //
  return (
    <div className="fixed bottom-24 right-6 z-50 flex h-[480px] w-80 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-blue-600 px-4 py-3 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-semibold transition-all duration-300 ${
              handoffTo ? "scale-110 ring-2 ring-white/70" : ""
            }`}
          >
            {agentInitial(handoffTo || currentAgent)}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">QRFlow Assistant</div>
            <div
              data-testid="agent-subtitle"
              className="text-xs text-blue-100 leading-tight"
            >
              {handoffTo
                ? `Connecting you with ${agentLabel(handoffTo)}…`
                : agentLabel(currentAgent)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          aria-label="Close chat"
          className="text-blue-100 transition-colors hover:text-white"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-gray-50 px-3 py-4">
        {messages.length === 0 && !isLoading && (
          <p className="mt-8 text-center text-sm text-gray-400">
            Ask me anything about QRFlow.
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
          >
            {m.role === "assistant" && m.responseType === "action_pending" ? (
              <div
                data-testid="action-pending-card"
                className="max-w-[85%] rounded-lg border-2 border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-gray-800"
              >
                <p className="whitespace-pre-wrap">{m.content}</p>
                {m.pendingAction && (
                  <p className="mt-1 text-xs font-medium text-yellow-800">
                    {describeAction(m.pendingAction)}
                  </p>
                )}
                {m.resolved ? (
                  <p className="mt-2 text-xs italic text-gray-500">
                    {m.resolved === "confirmed" ? "✓ Confirmed" : "✗ Cancelled"}
                  </p>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => resolvePendingAction(i, true, m.pendingAction)}
                      disabled={isLoading}
                      className="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => resolvePendingAction(i, false, null)}
                      disabled={isLoading}
                      className="rounded-md bg-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-blue-600 text-white"
                    : "border border-gray-200 bg-white text-gray-800"
                }`}
              >
                {m.content}
              </div>
            )}
            <span className="mt-0.5 text-[10px] text-gray-400">{formatTime(m.time)}</span>
          </div>
        ))}

        {isLoading && (
          <div className="flex items-center gap-1 px-1" data-testid="chat-loading">
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
          </div>
        )}

        {hasError && (
          <div className="flex flex-col items-start gap-1">
            <div className="max-w-[85%] rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              Something went wrong. Try again.
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        )}

        {csatState && (
          <div
            data-testid="csat-widget"
            className="mt-2 flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-3 text-center"
          >
            {csatState === "done" ? (
              <p className="text-sm font-medium text-gray-700">
                Thank you for your feedback!
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500">How was this conversation?</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => rateConversation(n)}
                      disabled={csatState === "submitting"}
                      aria-label={`Rate ${n} ${n === 1 ? "star" : "stars"}`}
                      className="text-2xl leading-none text-yellow-400 transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      ★
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Agent switch */}
      <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-1.5 text-xs text-gray-400">
        <span>Talk to</span>
        {SWITCHABLE_AGENTS.filter((a) => a !== currentAgent).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => switchAgent(a)}
            className="font-medium text-blue-600 hover:text-blue-700"
          >
            {agentLabel(a)}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 border-t border-gray-200 bg-white px-3 py-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          placeholder="Type a message…"
          aria-label="Message"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={isLoading || input.trim() === ""}
          className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
}

export default ChatWidget;
